/**
 * AdminLayout — authenticated shell: role-aware nav (desktop sidebar + mobile drawer).
 */
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, LogOut, ShieldCheck, MapPin, Store, Image, Settings, Users, Menu, KeyRound } from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { ChangePasswordGate } from '@/components/auth/ChangePasswordGate';
import { ChangeMyPasswordDialog } from '@/components/auth/ChangeMyPasswordDialog';
import logoWhite from '@/assets/brand/vivilazona-logo-white.svg';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { NAV, LOGOUT, ADMIN_MANAGE } from '@/constants/testIds';

const ROLE_LABELS = {
	super_admin: 'Super Admin',
	municipality_admin: 'Amministratore Comune',
	municipality_operator: 'Operatore Comune',
	local_business: 'Attività Locale',
	visitor: 'Visitatore',
};

export function AdminLayout() {
        const { user, logout, hasRole, can } = useAuth();
        const navigate = useNavigate();
        const [drawerOpen, setDrawerOpen] = useState(false);
        const [pwdOpen, setPwdOpen] = useState(false);

        const onLogout = async () => {
                await logout();
                navigate('/login', { replace: true });
        };

        // Single source of truth for nav items (RBAC gated). Rendered in both surfaces.
        const items = [
                { to: '/admin', icon: LayoutDashboard, label: 'Eventi', testId: NAV.dashboardLink, end: true, show: true },
                { to: '/admin/places', icon: MapPin, label: 'Luoghi', testId: ADMIN_MANAGE.navPlaces, show: can('manage_content') },
                { to: '/admin/activities', icon: Store, label: 'Attività', testId: ADMIN_MANAGE.navActivities, show: true },
                { to: '/admin/media', icon: Image, label: 'Media', testId: ADMIN_MANAGE.navMedia, show: true },
                { to: '/admin/users', icon: Users, label: 'Utenti', testId: NAV.usersLink, show: hasRole('municipality_admin') },
                { to: '/admin/config', icon: Settings, label: 'Configurazione', testId: NAV.settingsLink, show: hasRole('municipality_admin') },
                { to: '/admin/municipalities', icon: Building2, label: 'Comuni', testId: NAV.municipalitiesLink, show: hasRole('super_admin') },
        ].filter((i) => i.show);

        const renderItem = ({ to, icon: Icon, label, testId, end }, onClick) => (
                <NavLink
                        key={to}
                        to={to}
                        end={end}
                        data-testid={testId}
                        onClick={onClick}
                        className={({ isActive }) =>
                                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                                        isActive ? 'bg-emerald-400/10 text-emerald-300' : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
                                }`
                        }
                >
                        <Icon size={17} />
                        {label}
                </NavLink>
        );

        const RoleCard = () => (
                <div className="mt-auto rounded-lg border border-slate-800 bg-slate-900/50 p-3">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                                <ShieldCheck size={14} className="text-emerald-300" />
                                <span data-testid={NAV.roleBadge}>{ROLE_LABELS[user?.role] || user?.role}</span>
                        </div>
                        <p className="mt-1 truncate text-sm font-medium text-slate-200">{user?.full_name}</p>
                        {!user?.must_change_password && (
                        <button
                                onClick={() => { setDrawerOpen(false); setPwdOpen(true); }}
                                data-testid="change-my-password-button"
                                className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-slate-700 py-1.5 text-xs text-slate-300 transition-colors hover:border-emerald-400/40 hover:text-emerald-300"
                        >
                                <KeyRound size={13} /> Cambia la mia password
                        </button>
                        )}
                        <button
                                onClick={onLogout}
                                data-testid={LOGOUT.button}
                                className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-slate-700 py-1.5 text-xs text-slate-300 transition-colors hover:border-rose-400/40 hover:text-rose-300"
                        >
                                <LogOut size={13} /> Esci
                        </button>
                </div>
        );

        return (
                <div className="min-h-screen bg-[#0b1120] text-slate-100" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
                        <div className="flex">
                                {/* Desktop sidebar */}
                                <aside data-testid={NAV.shell} className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-slate-800 bg-slate-950/60 p-4 lg:flex">
                                        <div className="mb-8 flex items-center px-2">
                                                <img src={logoWhite} alt="ViviLaZona" className="h-8 w-auto" />
                                        </div>
                                        <nav className="flex flex-col gap-1">{items.map((i) => renderItem(i))}</nav>
                                        <RoleCard />
                                </aside>

                                {/* Content */}
                                <main className="flex-1 lg:pl-64">
                                        {/* Mobile top bar with drawer trigger */}
                                        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 lg:hidden">
                                                <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                                                        <SheetTrigger asChild>
                                                                <button data-testid={NAV.mobileMenuButton} aria-label="Apri menu" className="flex items-center gap-2 text-slate-300">
                                                                        <Menu size={20} />
                                                                        <img src={logoWhite} alt="ViviLaZona" className="h-6 w-auto" />
                                                                </button>
                                                        </SheetTrigger>
                                                        <SheetContent side="left" data-testid={NAV.mobileDrawer} className="flex w-72 flex-col border-slate-800 bg-slate-950 p-4 text-slate-100">
                                                                <SheetTitle className="mb-4 flex items-center gap-2 text-slate-100">
                                                                        <img src={logoWhite} alt="ViviLaZona" className="h-7 w-auto" />
                                                                </SheetTitle>
                                                                <nav className="flex flex-col gap-1">
                                                                        {items.map((i) => renderItem(i, () => setDrawerOpen(false)))}
                                                                </nav>
                                                                <RoleCard />
                                                        </SheetContent>
                                                </Sheet>
                                                <button onClick={onLogout} data-testid="logout-button-mobile" className="text-xs text-slate-400">
                                                        Esci
                                                </button>
                                        </div>
                                        <div data-testid={NAV.userMenu} className="px-6 py-8 sm:px-10">
                                                <Outlet />
                                        </div>
                                        <ChangePasswordGate />
                                        {!user?.must_change_password && (
                                                <ChangeMyPasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} onChanged={async () => { setPwdOpen(false); await onLogout(); }} />
                                        )}
                                </main>
                        </div>
                </div>
        );
}

export default AdminLayout;
